import React, { useEffect, useState } from 'react';
import { getBrandLogo } from '../../../utils/assetUtils';
import { AddToBagButton } from './AddToBagButton';
import { useConciergeContext } from '../../ConciergeContextProvider';
import type { ConciergeProductV2Detail } from '../../types';
import { trackRetailerProductPageOpen } from '../../tracking/trackRetailerProductPageOpen';
import { formatMoney, isMarketplaceRetailer, retailerLogoUrl } from './helpers';
import { BRAND_GUTTER, CHIP_BG, INK, MUTED } from './constants';

/**
 * Resolve the displayed price from the active promotion when present:
 * `promoPrice` is the deal price, `listPrice` the strikethrough, and
 * `discountPercentage` is a fraction (0.30 -> "30% savings"). Falls back to
 * `retailPrice` with no savings when there's no active promotion.
 */
function getDisplayPrice(detail: ConciergeProductV2Detail): {
  price: number;
  originalPrice: number | null;
  savingsPercent: number | null;
} {
  const promotion = detail.activeProductPromotion ?? detail.productPromotions?.find((candidate) => candidate.isActive);
  if (promotion?.isActive && promotion.promoPrice > 0) {
    const price = promotion.promoPrice;
    const originalPrice = promotion.listPrice > price ? promotion.listPrice : null;
    const savingsPercent = promotion.discountPercentage
      ? Math.round(promotion.discountPercentage * 100)
      : originalPrice
        ? Math.round(((originalPrice - price) / originalPrice) * 100)
        : null;
    return { price, originalPrice, savingsPercent };
  }
  return { price: detail.retailPrice ?? 0, originalPrice: null, savingsPercent: null };
}

export function PriceBlock({ detail }: { detail: ConciergeProductV2Detail }) {
  const { price, originalPrice, savingsPercent } = getDisplayPrice(detail);
  const { commerce } = useConciergeContext();
  // Set when an add is REFUSED (no fulfillment route for the sku, say). The
  // server owns that answer and only gives it at add time, so the slot falls
  // back to "View" rather than leaving a hole where the button was.
  const [bagRefused, setBagRefused] = useState(false);
  useEffect(() => setBagRefused(false), [detail.stacklineSku]);

  // One action slot, two possible occupants. When this app has a bag that can
  // take THIS product, buying beats looking, so "Add to bag" takes the slot and
  // the link-out moves to the product title (see `ProductDetailPanel`). With no
  // bag — no CHECKOUT capability and not a Shopify surface — the slot keeps the
  // plain "View" link, which is all we can offer. Never both: two buttons in one
  // row is how a shopper ends up at the retailer with an abandoned bag here.
  const canAddToBag =
    !bagRefused &&
    !!commerce &&
    commerce.canAddToCart({
      stacklineSku: detail.stacklineSku,
      retailerId: detail.retailerId,
      retailerProductPageUrl: detail.retailerProductPageUrl,
      retailerSku: detail.retailerSku,
      isAvailable: detail.isAvailable,
    });

  // The primary offer's seller: a marketplace product is sold *at a retailer* (Amazon,
  // Target, …), so show that retailer (logo from retailerId + retailerName). Only a
  // DTC/brand store (no marketplace retailerId) is the brand's own store — fall back to
  // the brand there. Don't assume the primary is always the brand's store.
  const marketplace = isMarketplaceRetailer(detail.retailerId);
  const sellerLogo = marketplace
    ? retailerLogoUrl(detail.retailerId)
    : detail.brandId
      ? getBrandLogo(detail.brandId)
      : null;
  const sellerName = (marketplace ? detail.retailerName : detail.brandName) || detail.brandName || detail.retailerName;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          {sellerLogo && (
            <img
              src={sellerLogo}
              alt=""
              aria-hidden="true"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
              style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'contain', flexShrink: 0 }}
            />
          )}
          {sellerName && (
            <span
              style={{
                fontSize: 16,
                fontWeight: 500,
                lineHeight: '20px',
                color: INK,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {sellerName}
            </span>
          )}
        </div>
        {canAddToBag ? (
          <AddToBagButton detail={detail} onUnavailable={() => setBagRefused(true)} />
        ) : (
          detail.retailerProductPageUrl && (
            <a
              href={detail.retailerProductPageUrl}
              target="_blank"
              rel="noreferrer"
              // The shopper is leaving for the retailer here — this is the click the
              // external-order pipeline keys on. See `trackRetailerProductPageOpen`.
              onClick={() =>
                trackRetailerProductPageOpen({
                  stacklineSku: detail.stacklineSku,
                  retailerSku: detail.retailerSku,
                  retailerId: detail.retailerId,
                  retailerName: detail.retailerName,
                  retailPrice: price,
                  retailerProductPageUrl: detail.retailerProductPageUrl,
                  isAvailable: detail.isAvailable,
                })
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 16px',
                borderRadius: 100,
                background: INK,
                color: '#ffffff',
                fontFamily: 'inherit',
                fontSize: 12,
                fontWeight: 500,
                lineHeight: '20px',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
            >
              View
            </a>
          )
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: BRAND_GUTTER }}>
        <span style={{ fontSize: 14, fontWeight: 600, lineHeight: '20px', color: INK }}>{formatMoney(price)}</span>
        {originalPrice != null && (
          <span
            style={{ fontSize: 10, fontWeight: 400, lineHeight: '16px', color: MUTED, textDecoration: 'line-through' }}
          >
            {formatMoney(originalPrice)}
          </span>
        )}
        {savingsPercent != null && savingsPercent > 0 && (
          <span
            style={{
              background: CHIP_BG,
              borderRadius: 100,
              padding: '2px 8px',
              fontSize: 10,
              fontWeight: 500,
              lineHeight: '20px',
              whiteSpace: 'nowrap',
              color: INK,
            }}
          >
            {savingsPercent}% savings
          </span>
        )}
      </div>
    </div>
  );
}
